/**
 * visibilityAwareInterval
 *
 * Wrapper around setInterval that automatically pauses while the browser tab
 * is hidden (`document.hidden === true`) and resumes when it becomes visible
 * again. On the hidden→visible transition, fires one immediate `onResume`
 * callback (defaults to the same `tick` fn) so layers refresh stale data the
 * moment the user looks at them.
 *
 * Why it exists: the layers2/ classes (WFS, GeoJSON, LiveGeoJSON, VAR — and
 * soon WMS/TMS) all polled with bare setInterval, which keeps firing in
 * background tabs. With 13+ production layers polling concurrently this
 * burned bandwidth and DB load for users who left EM3 open overnight.
 *
 * One shared `visibilitychange` listener handles all instances; reference
 * counted so we add the listener once and remove it when the last subscriber
 * cancels.
 */

type Tick = () => void;

interface Options {
  /**
   * Fired once on hidden→visible transition (before the timer re-arms).
   * Defaults to the `tick` callback. Pass a no-op to suppress catch-up
   * refreshes on resume.
   */
  onResume?: Tick;
}

export interface VisibilityAwareIntervalHandle {
  /** Stops the interval and detaches from the shared visibility listener. */
  cancel(): void;
}

interface Subscriber {
  tick: Tick;
  onResume: Tick;
  intervalMs: number;
  timer: ReturnType<typeof setInterval> | null;
}

const subscribers = new Set<Subscriber>();
let visibilityListenerAttached = false;

function isHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden === true;
}

function startTimer(sub: Subscriber): void {
  if (sub.timer !== null) return;
  sub.timer = setInterval(sub.tick, sub.intervalMs);
}

function stopTimer(sub: Subscriber): void {
  if (sub.timer === null) return;
  clearInterval(sub.timer);
  sub.timer = null;
}

function handleVisibilityChange(): void {
  if (isHidden()) {
    subscribers.forEach(stopTimer);
  } else {
    subscribers.forEach(sub => {
      try { sub.onResume(); } catch (e) { console.error('[visibilityAwareInterval] onResume threw', e); }
      startTimer(sub);
    });
  }
}

function ensureListener(): void {
  if (visibilityListenerAttached || typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerAttached = true;
}

function detachListenerIfEmpty(): void {
  if (subscribers.size > 0 || !visibilityListenerAttached) return;
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerAttached = false;
}

export function visibilityAwareInterval(
  tick: Tick,
  intervalMs: number,
  opts: Options = {},
): VisibilityAwareIntervalHandle {
  const sub: Subscriber = {
    tick,
    onResume: opts.onResume ?? tick,
    intervalMs,
    timer: null,
  };
  subscribers.add(sub);
  ensureListener();

  // Only arm the timer immediately if the tab is currently visible. If
  // hidden, the visibilitychange handler will arm it on next focus.
  if (!isHidden()) startTimer(sub);

  return {
    cancel() {
      stopTimer(sub);
      subscribers.delete(sub);
      detachListenerIfEmpty();
    },
  };
}

/**
 * Test-only: reset the module's internal state. Idempotent.
 */
export function _resetVisibilityAwareIntervalForTest(): void {
  subscribers.forEach(stopTimer);
  subscribers.clear();
  if (visibilityListenerAttached && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }
  visibilityListenerAttached = false;
}
